import * as go from 'gojs';
import { PanelNames } from './panelNames';
import {
  ARROW_LENGTH,
  ARROW_OFFSET,
  SMART_SELECT_BUTTON_HEIGHT,
  SMART_SELECT_BUTTON_WIDTH,
  SMART_SELECT_LEFT_TOOLBAR_CLEARANCE,
  SMART_SELECT_PANEL_GAP,
  SMART_SELECT_PANEL_HEIGHT,
  SMART_SELECT_PANEL_WIDTH,
  SMART_SELECT_PLACEMENT_PREVIEW_SIZE,
  SMART_SELECT_VIEWPORT_PADDING,
} from './smartSelectConstants';

export const isRealPoint = (point: go.Point) =>
  Number.isFinite(point.x) && Number.isFinite(point.y);

const isRealRect = (rect: go.Rect) =>
  Number.isFinite(rect.x) &&
  Number.isFinite(rect.y) &&
  Number.isFinite(rect.width) &&
  Number.isFinite(rect.height);

export const containsPointWithPadding = (
  rect: go.Rect,
  point: go.Point,
  padding: number,
) => {
  if (!isRealRect(rect)) {
    return false;
  }

  return (
    point.x >= rect.x - padding &&
    point.x <= rect.right + padding &&
    point.y >= rect.y - padding &&
    point.y <= rect.bottom + padding
  );
};

const getUnitBodyBounds = (node: go.Node) => {
  const body =
    node.findObject(PanelNames.UNIT_NODE_SELECTION_SHAPE) ??
    node.findObject('SHAPE') ??
    node.locationObject;

  const bodyBounds = body?.getDocumentBounds();

  if (bodyBounds && isRealRect(bodyBounds)) {
    return bodyBounds;
  }

  return node.actualBounds;
};

const getDirection = (node: go.Node, port: go.GraphObject) => {
  const bodyBounds = getUnitBodyBounds(node);
  const portCenter = port.getDocumentPoint(go.Spot.Center);

  if (!isRealRect(bodyBounds)) {
    return { unitX: 0, unitY: -1 };
  }

  const distances = [
    {
      distance: Math.abs(portCenter.x - bodyBounds.x),
      unitX: -1,
      unitY: 0,
    },
    {
      distance: Math.abs(portCenter.x - bodyBounds.right),
      unitX: 1,
      unitY: 0,
    },
    {
      distance: Math.abs(portCenter.y - bodyBounds.y),
      unitX: 0,
      unitY: -1,
    },
    {
      distance: Math.abs(portCenter.y - bodyBounds.bottom),
      unitX: 0,
      unitY: 1,
    },
  ];

  const closestSide = distances.reduce((closest, current) =>
    current.distance < closest.distance ? current : closest,
  );

  if (!Number.isFinite(closestSide.distance)) {
    return { unitX: 0, unitY: -1 };
  }

  return closestSide;
};

export const getArrowPoints = (node: go.Node, port: go.GraphObject) => {
  const portCenter = port.getDocumentPoint(go.Spot.Center);

  if (!isRealPoint(portCenter)) {
    return null;
  }

  const direction = getDirection(node, port);
  const start = new go.Point(
    portCenter.x + direction.unitX * ARROW_OFFSET,
    portCenter.y + direction.unitY * ARROW_OFFSET,
  );
  const end = new go.Point(
    start.x + direction.unitX * ARROW_LENGTH,
    start.y + direction.unitY * ARROW_LENGTH,
  );

  return { direction, start, end };
};

export const getSmartSelectButtonPosition = (
  end: go.Point,
  direction: ReturnType<typeof getDirection>,
) => {
  const gap = 8;

  if (direction.unitX > 0) {
    return new go.Point(end.x + gap, end.y - SMART_SELECT_BUTTON_HEIGHT / 2);
  }

  if (direction.unitX < 0) {
    return new go.Point(
      end.x - SMART_SELECT_BUTTON_WIDTH - gap,
      end.y - SMART_SELECT_BUTTON_HEIGHT / 2,
    );
  }

  if (direction.unitY < 0) {
    return new go.Point(
      end.x - SMART_SELECT_BUTTON_WIDTH / 2,
      end.y - SMART_SELECT_BUTTON_HEIGHT - gap,
    );
  }

  return new go.Point(end.x - SMART_SELECT_BUTTON_WIDTH / 2, end.y + gap);
};

const clamp = (value: number, min: number, max: number) => {
  if (max < min) {
    return min;
  }

  return Math.min(Math.max(value, min), max);
};

const expandRect = (rect: go.Rect, padding: number) =>
  new go.Rect(
    rect.x - padding,
    rect.y - padding,
    rect.width + padding * 2,
    rect.height + padding * 2,
  );

const unionRects = (first: go.Rect, second: go.Rect) => {
  const x = Math.min(first.x, second.x);
  const y = Math.min(first.y, second.y);
  const right = Math.max(first.right, second.right);
  const bottom = Math.max(first.bottom, second.bottom);

  return new go.Rect(x, y, right - x, bottom - y);
};

const getIntersectionArea = (first: go.Rect, second: go.Rect) => {
  const x = Math.max(first.x, second.x);
  const y = Math.max(first.y, second.y);
  const right = Math.min(first.right, second.right);
  const bottom = Math.min(first.bottom, second.bottom);

  if (right <= x || bottom <= y) {
    return 0;
  }

  return (right - x) * (bottom - y);
};

export const getRectCenter = (rect: go.Rect) =>
  new go.Point(rect.x + rect.width / 2, rect.y + rect.height / 2);

type SmartSelectPanelDimensions = {
  width: number;
  height: number;
  scale: number;
};

const getSmartSelectViewportBounds = (diagram: go.Diagram) => {
  const viewportBounds = diagram.viewportBounds;
  const toolbarBoundary = diagram.transformViewToDoc(
    new go.Point(SMART_SELECT_LEFT_TOOLBAR_CLEARANCE, 0),
  ).x;
  const left = Math.max(viewportBounds.x, toolbarBoundary);

  return new go.Rect(
    left,
    viewportBounds.y,
    Math.max(0, viewportBounds.right - left),
    viewportBounds.height,
  );
};

export const getSmartSelectPanelDimensions = (
  diagram?: go.Diagram | null,
): SmartSelectPanelDimensions => {
  if (!diagram) {
    return {
      width: SMART_SELECT_PANEL_WIDTH,
      height: SMART_SELECT_PANEL_HEIGHT,
      scale: 1,
    };
  }

  const viewportBounds = getSmartSelectViewportBounds(diagram);
  const availableWidth = Math.max(
    0,
    viewportBounds.width - SMART_SELECT_VIEWPORT_PADDING * 2,
  );
  const availableHeight = Math.max(
    0,
    viewportBounds.height - SMART_SELECT_VIEWPORT_PADDING * 2,
  );
  const scale = Math.min(
    1,
    availableWidth / SMART_SELECT_PANEL_WIDTH,
    availableHeight / SMART_SELECT_PANEL_HEIGHT,
  );

  return {
    width: SMART_SELECT_PANEL_WIDTH * scale,
    height: SMART_SELECT_PANEL_HEIGHT * scale,
    scale,
  };
};

export const clampPointToViewport = (
  point: go.Point,
  diagram: go.Diagram,
  width: number,
  height: number,
) => {
  const viewportBounds = getSmartSelectViewportBounds(diagram);

  return new go.Point(
    clamp(
      point.x,
      viewportBounds.x + SMART_SELECT_VIEWPORT_PADDING,
      viewportBounds.right - width - SMART_SELECT_VIEWPORT_PADDING,
    ),
    clamp(
      point.y,
      viewportBounds.y + SMART_SELECT_VIEWPORT_PADDING,
      viewportBounds.bottom - height - SMART_SELECT_VIEWPORT_PADDING,
    ),
  );
};

export const getPlacementPreviewBounds = (
  node: go.Node,
  port: go.GraphObject,
) => {
  const arrowPoints = getArrowPoints(node, port);
  const portCenter = port.getDocumentPoint(go.Spot.Center);
  const size = SMART_SELECT_PLACEMENT_PREVIEW_SIZE;

  if (!arrowPoints || !isRealPoint(portCenter)) {
    const bodyBounds = getUnitBodyBounds(node);
    return new go.Rect(bodyBounds.right, bodyBounds.y, size, size);
  }

  const { direction, end } = arrowPoints;
  let x = portCenter.x - size / 2;
  let y = portCenter.y - size / 2;

  if (direction.unitX > 0) {
    x = end.x;
  } else if (direction.unitX < 0) {
    x = end.x - size;
  } else if (direction.unitY > 0) {
    y = end.y;
  } else {
    y = end.y - size;
  }

  return new go.Rect(x, y, size, size);
};

export const getPlacementArrowPoints = (
  node: go.Node,
  port: go.GraphObject,
) => {
  const arrowPoints = getArrowPoints(node, port);

  if (!arrowPoints) {
    return null;
  }

  const previewBounds = getPlacementPreviewBounds(node, port);
  const previewCenter = getRectCenter(previewBounds);
  const { direction, start } = arrowPoints;
  let end = previewCenter;

  if (direction.unitX > 0) {
    end = new go.Point(previewBounds.x, previewCenter.y);
  } else if (direction.unitX < 0) {
    end = new go.Point(previewBounds.right, previewCenter.y);
  } else if (direction.unitY > 0) {
    end = new go.Point(previewCenter.x, previewBounds.y);
  } else {
    end = new go.Point(previewCenter.x, previewBounds.bottom);
  }

  return { end, start };
};

const getPanelCandidatePositions = (
  occupiedBounds: go.Rect,
  previewBounds: go.Rect,
  direction: ReturnType<typeof getDirection>,
  panelDimensions: SmartSelectPanelDimensions,
) => {
  const previewCenter = getRectCenter(previewBounds);
  const occupiedCenter = getRectCenter(occupiedBounds);
  const right = new go.Point(
    previewBounds.right + SMART_SELECT_PANEL_GAP,
    previewCenter.y - panelDimensions.height / 2,
  );
  const left = new go.Point(
    previewBounds.x - panelDimensions.width - SMART_SELECT_PANEL_GAP,
    previewCenter.y - panelDimensions.height / 2,
  );
  const below = new go.Point(
    occupiedCenter.x - panelDimensions.width / 2,
    occupiedBounds.bottom + SMART_SELECT_PANEL_GAP,
  );
  const above = new go.Point(
    occupiedCenter.x - panelDimensions.width / 2,
    occupiedBounds.y - panelDimensions.height - SMART_SELECT_PANEL_GAP,
  );

  if (direction.unitX > 0) {
    return [right, below, above, left];
  }

  if (direction.unitX < 0) {
    return [left, below, above, right];
  }

  if (direction.unitY > 0) {
    return [below, right, left, above];
  }

  return [above, right, left, below];
};

export const getSmartSelectPanelPosition = (
  node: go.Node,
  port: go.GraphObject,
) => {
  const arrowPoints = getArrowPoints(node, port);
  const diagram = node.diagram;

  if (!arrowPoints || !diagram) {
    return node.actualBounds.position;
  }

  const sourceBounds = getUnitBodyBounds(node);
  const previewBounds = getPlacementPreviewBounds(node, port);
  const panelDimensions = getSmartSelectPanelDimensions(diagram);
  const occupiedBounds = expandRect(
    unionRects(sourceBounds, previewBounds),
    SMART_SELECT_PANEL_GAP,
  );
  const candidates = getPanelCandidatePositions(
    occupiedBounds,
    previewBounds,
    arrowPoints.direction,
    panelDimensions,
  );
  let bestPosition = candidates[0];
  let bestOverlap = Number.POSITIVE_INFINITY;
  let bestDistance = Number.POSITIVE_INFINITY;

  for (const candidate of candidates) {
    const clampedPosition = clampPointToViewport(
      candidate,
      diagram,
      panelDimensions.width,
      panelDimensions.height,
    );
    const panelBounds = new go.Rect(
      clampedPosition.x,
      clampedPosition.y,
      panelDimensions.width,
      panelDimensions.height,
    );
    const overlap = getIntersectionArea(panelBounds, occupiedBounds);
    const distance = clampedPosition.distanceSquaredPoint(candidate);

    if (
      overlap < bestOverlap ||
      (overlap === bestOverlap && distance < bestDistance)
    ) {
      bestPosition = clampedPosition;
      bestOverlap = overlap;
      bestDistance = distance;
    }

    if (overlap === 0 && distance === 0) {
      break;
    }
  }

  return bestPosition;
};
